"use client";

import React from "react";
import { Camera, PenTool, Signature, Instagram, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const journalEntries = [{
  date: "October 14, 2008",
  title: "The First Roll",
  story: "The first time I held the Leica M6, it felt cold and heavy. Not a burden, but an anchor. I walked the streets until my feet bled, looking for light in the shadows. There is a silence in analog photography that digital cannot replicate—a pause between the shutter click and the realization of the image.",
  technical: "Shot on Kodak Tri-X 400. Pushed to 1600. Developed in Rodinal 1:50 for high acutance and pronounced grain structure."
}, {
  date: "March 02, 2015",
  title: "Sanctuary in Red",
  story: "The darkroom became my sanctuary. The smell of fixer, the hum of the ventilation, the red safety light—it was a ritual. Watching an image emerge from the void on fiber paper taught me a patience that the modern world had stolen. It wasn't just about making a picture; it was about crafting an object.",
  technical: "Print details: 11x14 fiber-based paper. Split-grade printing technique. Dodge and burn map: +2s shadows, -1s highlights."
}, {
  date: "Present Day",
  title: "The Narrative Arc",
  story: "Now, it is entirely about the narrative. Every frame must earn its place on the roll. We don't just take photos; we preserve moments of time that would otherwise slip away unnoticed. The grain is the texture of memory itself.",
  technical: "Current kit: Hasselblad 500C/M. 80mm Planar T*. Portra 400 for color work, Ilford HP5 for the soul."
}];
export default function BehindTheLens() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="behind-the-lens" ref={sectionRef} className={`dark bg-background text-foreground py-20 md:py-32 overflow-hidden transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        {}
        <div className="mb-16 md:mb-24 space-y-6 text-center md:text-left">
          <h2 className="text-4xl md:text-6xl font-serif tracking-tight text-primary">
            Behind the Lens
          </h2>
          <p className="text-muted-foreground max-w-2xl text-lg font-sans font-light">
            A look into the process, the chemistry, and the philosophy behind the frames.
          </p>
        </div>

        {}
        <div className="mb-20 relative">
          <div className="absolute inset-0 bg-primary/5 rounded-sm transform translate-x-2 translate-y-2 md:translate-x-4 md:translate-y-4 -z-10" />
          <AspectRatio ratio={21 / 9} className="bg-muted rounded-sm overflow-hidden shadow-2xl">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556059/img/messy_darkroom_wide.png" alt="A messy darkroom workbench covered in contact sheets, film rolls, and developing tanks" className="object-cover w-full h-full opacity-90 hover:opacity-100 transition-opacity duration-700" />
            {}
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
            
            {}
            <div className="absolute bottom-4 left-4 md:bottom-8 md:left-8 bg-background/90 backdrop-blur-sm px-4 py-2 border border-border/50 rounded-sm">
              <p className="text-xs font-mono text-muted-foreground uppercase tracking-widest">
                Workspace / Darkroom 01
              </p>
            </div>
          </AspectRatio>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-16 mb-24">
          {journalEntries.map((entry, i) => <div key={i} data-index={i} className={`group flex flex-col gap-4 ${i === 2 ? "md:col-span-2 md:w-2/3 md:mx-auto text-center md:text-left" : ""}`}>
              <div className="flex items-center gap-3 text-muted-foreground/60 mb-2">
                <span className="font-mono text-xs uppercase tracking-widest border-b border-border pb-1">
                  {entry.date}
                </span>
              </div>
              
              <h3 className="text-2xl font-serif text-foreground group-hover:text-primary transition-colors duration-300">
                {entry.title}
              </h3>

              <div className="space-y-6">
                <p className="font-serif text-lg leading-relaxed text-muted-foreground/90">
                  {entry.story}
                </p>
                
                <div className="relative pl-4 border-l-2 border-primary/20">
                  <p className="font-sans text-sm text-muted-foreground italic">
                    <span className="font-semibold not-italic text-foreground/80 block mb-1 text-xs uppercase tracking-wide">
                      Technical Note
                    </span>
                    {entry.technical}
                  </p>
                </div>
              </div>
            </div>)}
        </div>

        {}
        <div className="flex justify-center">
          <Card className="w-full max-w-md bg-card/50 backdrop-blur-sm border-border/50 shadow-xl relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-primary" />
            <CardContent className="pt-8 pb-8 px-8 flex flex-col items-center text-center gap-6">
              
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-muted overflow-hidden border-2 border-primary/20">
                   {}
                   <div className="w-full h-full bg-secondary flex items-center justify-center text-muted-foreground">
                      <Camera className="w-8 h-8 opacity-50" />
                   </div>
                </div>
                <div className="absolute -bottom-2 -right-2 bg-background p-1.5 rounded-full border border-border shadow-sm">
                  <PenTool className="w-3 h-3 text-primary" />
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="text-xl font-serif text-foreground">Alex Carter</h4>
                <p className="text-xs font-mono text-muted-foreground uppercase tracking-widest">
                  Founder & Lead Photographer
                </p>
              </div>

              <Separator className="w-1/2 bg-border/50" />

              <div className="font-serif text-2xl italic text-foreground/80 transform -rotate-2">
                Alex Carter
              </div>

              <div className="flex gap-4 pt-2">
                <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-primary" asChild>
                  <Link to="https://instagram.com" newTab>
                    <Instagram className="w-4 h-4 mr-2" />
                    @filmframe
                  </Link>
                </Button>
                <Button variant="outline" size="sm" className="border-primary/20 hover:border-primary/50 hover:bg-primary/5" asChild>
                  <Link to="/TheArtistPage">
                    Read Full Bio
                    <ArrowRight className="w-3 h-3 ml-2" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}